import { useMutation, useQuery } from '@apollo/client/react';
import { Accordion, Badge, Box, Button, Card, Heading, HStack, Icon, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { LegalDocument, LegalDocumentFields } from '@src/components/sco/Advisors/cards/LegalDocument';
import { FAST_ADDITIONAL_TERMS_MAX_LENGTH, FAST_FIELD_HELPERS, FAST_HOURS_PER_MONTH_DEFAULT, FAST_HOURS_RANGE_LABEL, FAST_LEGAL_RECAP, FAST_VERSION, defaultFastLegalTerms, fastSecurityTypeLabel, normalizeAdditionalTerms, parseFastLegalTerms } from '@src/components/sco/Advisors/data/fastAgreement';
import { useAdvisorAgreementPdfDownload } from '@src/components/sco/Advisors/hooks/useAdvisorAgreementPdfDownload';
import { useCreateAdvisorAgreement } from '@src/components/sco/Advisors/hooks/useCreateAdvisorAgreement';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { DocChoiceGroup, DocCombobox, DocDateField, DocField, DocTextarea } from '@src/components/sco/Advisors/shared/DocField';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { RecapRow } from '@src/components/sco/Advisors/shared/RecapRow';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { StepBlock } from '@src/components/sco/Advisors/shared/StepBlock';
import type { AdvisorAgreement, AdvisorAgreementQueryResult, SendAdvisorAgreementResult, UpsertAdvisorAgreementResult } from '@src/components/sco/Advisors/types/advisorAgreement';
import { AGREEMENT_STATUS_DECLINED, AGREEMENT_STATUS_DRAFT, AGREEMENT_STATUS_SIGNED, advisorDisplayName, agreementStatusTitle, CALIBER_KEY_TO_LABEL, COMPANY_STAGE_KEY_TO_LABEL, CONTRIBUTION_TIER_KEY_TO_LABEL, formatAgreementDate, formatAgreementSigners, formatEquityPercent, WARM_INTRO_KEY_TO_LABEL } from '@src/components/sco/Advisors/utils/agreementKeys';
import { agreementSendErrorMessage, canDeleteUnsignedAdvisorAgreement, DELETE_UNSIGNED_AGREEMENT_CONFIRM, isAgreementAwaitingMarkSigned, sendAdvisorAgreementBlockedReason, SEND_FOUNDER_REQUIRED, SEND_PARTY_REQUIRED } from '@src/components/sco/Advisors/utils/agreementSend';
import { applyStandardFastTerms, areaOfAdviceChoiceItems, cliffMonthsChoiceItems, companySecuritySectionComplete, companyStageChoiceItems, emptyFastAgreementForm, engagementSectionComplete, equitySectionComplete, type FastAgreementDraftForm, formFromAgreement, formatDateOnlyLabel, governingLawSelectItems, hoursPerMonthChoiceItems, isAdvisorInitiated, normalizeInitiatingPartyKey, recipientSectionComplete, securityTypeChoiceItems, serviceLevelChoiceItems, termMonthsChoiceItems, termsSectionComplete, timingSectionComplete, toDateOnly, upsertPartyFieldsFromForm, vestingMonthsChoiceItems } from '@src/components/sco/Advisors/utils/fastAgreementForm';
import { advisorContractingPartyName, advisorSignatoryLabel, INITIATING_PARTY_OPTIONS, PARTY_FIELD_COPY, sendCtaLabel, withInitiatingParty } from '@src/components/sco/Advisors/utils/fastAgreementRecipient';
import { DELETE_ADVISOR_AGREEMENT } from '@src/gql/mutations/DeleteAdvisorAgreement';
import { SEND_ADVISOR_AGREEMENT } from '@src/gql/mutations/SendAdvisorAgreement';
import { SYNC_ADVISOR_AGREEMENT_SIGNATURE } from '@src/gql/mutations/SyncAdvisorAgreementSignature';
import { UPSERT_ADVISOR_AGREEMENT } from '@src/gql/mutations/UpsertAdvisorAgreement';
import { ADVISOR_AGREEMENT } from '@src/gql/queries/AdvisorAgreement';
import { ADVISOR_AGREEMENTS } from '@src/gql/queries/AdvisorAgreements';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { LuCalculator, LuCheck, LuDownload, LuPenLine, LuRefreshCw, LuSend, LuTrash2, LuZap } from 'react-icons/lu';
type DraftForm = FastAgreementDraftForm;
const emptyForm = (): DraftForm => emptyFastAgreementForm();
const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};
const optionTitle = (option: {
  title?: string | null;
  key?: string | null;
} | null | undefined, keyMap: Record<string, string>) => option?.title || (option?.key ? keyMap[option.key] : undefined) || '—';
const buildPreviewFields = (agreement: AdvisorAgreement | null | undefined, form: DraftForm): LegalDocumentFields => {
  const tierLabel = CONTRIBUTION_TIER_KEY_TO_LABEL[form.contributionTierKey] || optionTitle(agreement?.contributionTier, CONTRIBUTION_TIER_KEY_TO_LABEL);
  const stageLabel = COMPANY_STAGE_KEY_TO_LABEL[form.companyStageKey] || optionTitle(agreement?.companyStage, COMPANY_STAGE_KEY_TO_LABEL);
  const hours = form.hoursPerMonth.trim() || String(agreement?.hoursPerMonth ?? '');
  const vest = form.vestingMonths.trim() || String(agreement?.vestingMonths ?? '');
  const cliff = form.cliffMonths.trim() || String(agreement?.cliffMonths ?? '');
  const equity = form.equityPercent.trim() ? `${Number(form.equityPercent).toFixed(2)}%` : formatEquityPercent(agreement?.equityPercent);
  const advisorPersonName = form.advisorName.trim() || advisorDisplayName(agreement || {});
  const advisorEntityName = form.advisorEntityName.trim();
  const advisorName = advisorContractingPartyName(advisorPersonName, advisorEntityName) || advisorPersonName;
  const companyName = form.founderCompanyName.trim() || agreement?.company?.name || 'Your company';
  const founderName = form.founderName.trim() || agreement?.founderUser?.fullName || agreement?.founderUser?.displayName || 'Founder';
  const signed = agreement?.statusKey === AGREEMENT_STATUS_SIGNED;
  const legal = parseFastLegalTerms(agreement?.legalTermsAsJson);
  const startDateLabel = formatDateOnlyLabel(form.startDate) || formatDateOnlyLabel(agreement?.startDate) || 'TBD';
  return {
    startDateLabel,
    companyName,
    advisorName,
    expertiseLabel: form.advisorTitle.trim() || 'advisory services',
    engagementTierLabel: tierLabel !== '—' ? tierLabel : 'Strategic',
    hoursPerMonthLabel: hours ? `${hours} hours per month` : FAST_HOURS_RANGE_LABEL,
    companyStageLabel: stageLabel !== '—' ? stageLabel : undefined,
    governingLawLabel: form.governingLaw.trim() || legal.governingLaw || undefined,
    securityTypeLabel: fastSecurityTypeLabel(form.securityTypeKey || legal.securityType),
    equityPercentLabel: equity,
    vestingMonthsLabel: vest ? `${vest} months` : '—',
    cliffMonthsLabel: cliff ? `${cliff} month cliff` : '—',
    founderSignLabel: founderName,
    advisorSignLabel: advisorSignatoryLabel(advisorPersonName, advisorEntityName) || advisorName,
    founderSigned: signed,
    advisorSigned: signed,
    additionalTerms: form.additionalTerms.trim() || undefined
  };
};
export const AgreementSection = () => {
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const router = useRouter();
  const agreementId = typeof router.query.agreementId === 'string' ? router.query.agreementId : undefined;

  // Missing id (or a stale editor URL without one) goes to the list.
  useEffect(() => {
    if (!router.isReady || agreementId) {
      return;
    }
    void router.replace(paths.agreements);
  }, [agreementId, paths.agreements, router]);
  const [form, setForm] = useState<DraftForm>(emptyForm);
  const [activeIndex, setActiveIndex] = useState(0);
  const [prefilledId, setPrefilledId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sentFlash, setSentFlash] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const pdf = useAdvisorAgreementPdfDownload();
  const createAgreement = useCreateAdvisorAgreement();
  const agreementQuery = useQuery<AdvisorAgreementQueryResult>(ADVISOR_AGREEMENT, {
    skip: !router.isReady || !agreementId,
    variables: {
      advisorAgreementId: agreementId
    },
    fetchPolicy: 'network-only'
  });
  const [upsertAdvisorAgreement, upsertMutation] = useMutation<UpsertAdvisorAgreementResult>(UPSERT_ADVISOR_AGREEMENT);
  const [sendAdvisorAgreement, sendMutation] = useMutation<SendAdvisorAgreementResult>(SEND_ADVISOR_AGREEMENT);
  const [syncSignature, syncMutation] = useMutation(SYNC_ADVISOR_AGREEMENT_SIGNATURE);
  const [deleteAgreement, deleteMutation] = useMutation(DELETE_ADVISOR_AGREEMENT);

  // Ignore stale Apollo cache rows when switching agreementId (network-only still shows prior data).
  // Important: when agreementId is undefined, `?.id === undefined` is true — never read `.data`
  // without a local narrow (throws "Cannot read properties of undefined").
  const agreementRow = agreementQuery.data?.advisorAgreement;
  const agreement = agreementId && agreementRow?.advisorAgreementId === agreementId ? agreementRow : null;
  useEffect(() => {
    setPrefilledId(null);
    setForm(emptyForm());
    setSaveError(null);
    setSavedFlash(false);
    setSendError(null);
    setSentFlash(false);
    setStatusNote(null);
    setActiveIndex(0);
  }, [agreementId]);
  useEffect(() => {
    if (!agreement?.advisorAgreementId) {
      return;
    }
    if (prefilledId === agreement.advisorAgreementId) {
      return;
    }
    setForm(formFromAgreement(agreement));
    setPrefilledId(agreement.advisorAgreementId);
    setSaveError(null);
    setSavedFlash(false);
    setSendError(null);
    setSentFlash(false);
    setStatusNote(null);
  }, [agreement, prefilledId]);
  const canEdit = !!agreement?.currentUserCanEdit && agreement.statusKey === AGREEMENT_STATUS_DRAFT;
  const statusLabel = agreementStatusTitle(agreement?.statusKey, agreement?.status?.title);
  const advisorInitiated = isAdvisorInitiated(form.initiatingPartyKey);
  const selfParty = {
    name: currentUser?.fullName || currentUser?.displayName || '',
    email: currentUser?.email || ''
  };
  const displayName = advisorInitiated ? form.founderName.trim() || 'Founder' : form.advisorName.trim() || advisorDisplayName(agreement || {});
  const sendLabel = sendCtaLabel(form.initiatingPartyKey, displayName);
  const linkedSeatId = agreement?.advisorEngagementId || agreement?.engagement?.advisorEngagementId;
  const linkedSeatLabel = agreement?.engagement?.category?.title;
  const calculatorHref = paths.withQuery(paths.compCalculator, {
    engagementId: linkedSeatId,
    agreementId: canEdit ? agreement?.advisorAgreementId : undefined
  });
  const tierLabel = CONTRIBUTION_TIER_KEY_TO_LABEL[form.contributionTierKey] || optionTitle(agreement?.contributionTier, CONTRIBUTION_TIER_KEY_TO_LABEL);
  const stageLabel = COMPANY_STAGE_KEY_TO_LABEL[form.companyStageKey] || optionTitle(agreement?.companyStage, COMPANY_STAGE_KEY_TO_LABEL);
  const caliberLabel = optionTitle(agreement?.caliber, CALIBER_KEY_TO_LABEL);
  const introLabel = optionTitle(agreement?.warmIntro, WARM_INTRO_KEY_TO_LABEL);
  const previewFields = useMemo(() => buildPreviewFields(agreement, form), [agreement, form]);
  const setField = (key: keyof DraftForm) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: event.target.value
    }));
    setSavedFlash(false);
  };
  const setFormValue = (key_0: keyof DraftForm, value: string) => {
    setForm(prev_0 => ({
      ...prev_0,
      [key_0]: value
    }));
    setSavedFlash(false);
  };
  const handleApplyStandardTerms = () => {
    if (!canEdit) {
      return;
    }
    setForm(prev_1 => applyStandardFastTerms(prev_1, agreement));
    setSavedFlash(false);
  };
  const sendBlock = sendAdvisorAgreementBlockedReason({
    statusKey: agreement?.statusKey,
    currentUserCanEdit: agreement?.currentUserCanEdit,
    initiatingPartyKey: form.initiatingPartyKey,
    advisorUserId: agreement?.advisorUserId || agreement?.advisorUser?.userId,
    advisorEmail: form.advisorEmail.trim() || agreement?.advisorEmail || agreement?.advisorUser?.email,
    founderUserId: agreement?.founderUserId || agreement?.founderUser?.userId,
    founderEmail: form.founderEmail.trim() || agreement?.founderEmail || agreement?.founderUser?.email
  });
  const awaitingMarkSigned = isAgreementAwaitingMarkSigned(agreement?.statusKey);
  const signHref = agreementId ? paths.withQuery(paths.sign, {
    agreementId
  }) : paths.sign;
  const actionBusy = upsertMutation.loading || sendMutation.loading || deleteMutation.loading;
  const handleSave = async (): Promise<boolean> => {
    if (!agreementId || !canEdit) {
      return false;
    }
    setSaveError(null);
    const equityPercent = parseOptionalNumber(form.equityPercent);
    const vestingMonths = parseOptionalNumber(form.vestingMonths);
    const cliffMonths = parseOptionalNumber(form.cliffMonths);
    const hoursPerMonth = parseOptionalNumber(form.hoursPerMonth);
    const termMonths = parseOptionalNumber(form.termMonths);
    if (form.equityPercent.trim() && equityPercent == null) {
      setSaveError('Equity percent must be a number.');
      return false;
    }
    try {
      const result = await upsertAdvisorAgreement({
        variables: {
          input: {
            advisorAgreementId: agreementId,
            advisorEngagementId: linkedSeatId || agreement?.advisorEngagementId || null,
            ...upsertPartyFieldsFromForm(form, agreement?.company?.name),
            advisorTitle: form.advisorTitle.trim() || null,
            companyStageKey: form.companyStageKey || null,
            contributionTierKey: form.contributionTierKey || null,
            equityPercent,
            vestingMonths,
            cliffMonths,
            hoursPerMonth,
            termMonths,
            startDate: toDateOnly(form.startDate) || null,
            legalTermsAsJson: defaultFastLegalTerms({
              ...parseFastLegalTerms(agreement?.legalTermsAsJson),
              governingLaw: form.governingLaw.trim() || null,
              securityType: form.securityTypeKey,
              additionalTerms: normalizeAdditionalTerms(form.additionalTerms) || null
            }),
            fastTrackApplied: true
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENT,
          variables: {
            advisorAgreementId: agreementId
          }
        }],
        awaitRefetchQueries: true
      });
      const saved = result.data?.upsertAdvisorAgreement?.advisorAgreement;
      if (saved) {
        setForm(formFromAgreement(saved));
        setPrefilledId(saved.advisorAgreementId || agreementId);
        setSavedFlash(true);
        return true;
      }
      setSaveError('Could not save this draft.');
      return false;
    } catch (error) {
      setSaveError(agreementSendErrorMessage(error, 'Could not save this draft.'));
      return false;
    }
  };
  const handleSend = async () => {
    if (!agreementId || !canEdit) {
      return;
    }
    setSendError(null);
    if (sendBlock) {
      setSendError(sendBlock);
      return;
    }
    const saved_0 = await handleSave();
    if (!saved_0) {
      return;
    }
    try {
      await sendAdvisorAgreement({
        variables: {
          input: {
            advisorAgreementId: agreementId
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENT,
          variables: {
            advisorAgreementId: agreementId
          }
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: {
            role: 'FOUNDER',
            pageSize: 100,
            page: 1
          }
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: {
            role: 'ADVISOR',
            pageSize: 100,
            page: 1
          }
        }],
        awaitRefetchQueries: true
      });
      setSavedFlash(false);
      setSentFlash(true);
    } catch (error_0) {
      setSendError(agreementSendErrorMessage(error_0, 'Could not send this agreement.'));
    }
  };
  const handleCheckStatus = async () => {
    if (!agreementId) {
      return;
    }
    setSendError(null);
    setStatusNote(null);
    try {
      const result_0 = await syncSignature({
        variables: {
          input: {
            advisorAgreementId: agreementId
          }
        },
        refetchQueries: [{
          query: ADVISOR_AGREEMENT,
          variables: {
            advisorAgreementId: agreementId
          }
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: {
            role: 'FOUNDER',
            pageSize: 100,
            page: 1
          }
        }, {
          query: ADVISOR_AGREEMENTS,
          variables: {
            role: 'ADVISOR',
            pageSize: 100,
            page: 1
          }
        }],
        awaitRefetchQueries: true
      });
      const next = result_0.data?.syncAdvisorAgreementSignature?.advisorAgreement;
      if (next?.statusKey === AGREEMENT_STATUS_SIGNED) {
        setStatusNote('Both parties signed in Documenso. The signed PDF is attached.');
      } else if (next?.statusKey === AGREEMENT_STATUS_DECLINED) {
        setStatusNote('Documenso shows this envelope was declined.');
      } else {
        setStatusNote('Still waiting in Documenso. Sign, then check status again.');
      }
    } catch (error_1) {
      setSendError(agreementSendErrorMessage(error_1, 'Could not check Documenso status.'));
    }
  };
  const canDelete = canDeleteUnsignedAdvisorAgreement({
    currentUserRole: agreement?.currentUserRole,
    statusKey: agreement?.statusKey,
    currentUserCanDelete: agreement?.currentUserCanDelete
  });
  const handleDelete = async () => {
    if (!agreementId || !canDelete) {
      return;
    }
    if (!window.confirm(DELETE_UNSIGNED_AGREEMENT_CONFIRM)) {
      return;
    }
    setSendError(null);
    try {
      await deleteAgreement({
        variables: {
          input: {
            advisorAgreementId: agreementId
          }
        }
      });
      await router.replace(paths.agreements);
    } catch (error_2) {
      setSendError(agreementSendErrorMessage(error_2, 'Could not delete this agreement.'));
    }
  };
  if (!router.isReady || !agreementId || agreementQuery.loading && !agreement) {
    return <SectionScroll maxWidth={'3xl'}>
        <Text color={'colorPalette.fg/60'}>
          {agreementId ? 'Loading agreement…' : 'Redirecting to FAST Agreements…'}
        </Text>
      </SectionScroll>;
  }
  if (agreementQuery.error) {
    return <SectionScroll maxWidth={'3xl'}>
        <VStack align={'stretch'} gap={3}>
          <Heading size={'lg'}>Could not load agreement</Heading>
          <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
            {agreementQuery.error.message}
          </Text>
          <Button asChild alignSelf={'flex-start'} size={'sm'} variant={'outline'}>
            <a href={paths.agreements}>Back to FAST Agreements</a>
          </Button>
        </VStack>
      </SectionScroll>;
  }
  if (!agreement) {
    return <SectionScroll maxWidth={'3xl'}>
        <VStack align={'stretch'} gap={3}>
          <Heading size={'lg'}>Agreement not found</Heading>
          <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
            This draft may have been deleted, or you don&apos;t have access to it.
          </Text>
          {createAgreement.error ? <Text color={'red.fg'} fontSize={'sm'}>
              {createAgreement.error}
            </Text> : null}
          <HStack gap={2} wrap={'wrap'}>
            <Button loading={createAgreement.loading} size={'sm'} variant={'cta'} onClick={() => void createAgreement.create()}>
              Create a New Agreement
            </Button>
            <Button asChild size={'sm'} variant={'outline'}>
              <a href={paths.agreements}>FAST Agreements</a>
            </Button>
            <Button asChild size={'sm'} variant={'ghost'}>
              <a href={paths.compCalculator}>
                <LuCalculator /> Compensation Calculator
              </a>
            </Button>
          </HStack>
        </VStack>
      </SectionScroll>;
  }
  return <Box height={'full'} overflow={{
    base: 'auto',
    lg: 'hidden'
  }} width={'full'}>
      <SimpleGrid columns={{
      base: 1,
      lg: 2
    }} gap={0} height={{
      lg: 'full'
    }} minHeight={{
      lg: 0
    }}>
        {/* form */}
        <Box bgColor={'colorPalette.container/50'} height={{
        lg: 'full'
      }} minHeight={{
        lg: 0
      }} overflowY={{
        base: 'visible',
        lg: 'auto'
      }}>
          <VStack align={'stretch'} gap={4} padding={4}>
            <HStack align={'center'} gap={2} justify={'space-between'} wrap={'wrap'}>
              <HStack gap={2}>
                <Icon boxSize={5} color={'colorPalette.solid'}>
                  <LuPenLine />
                </Icon>
                <Heading size={'xl'}>FAST agreement builder</Heading>
              </HStack>
              {sentFlash ? <Badge colorPalette={'green'} variant={'subtle'}>
                  <LuCheck /> Sent
                </Badge> : savedFlash ? <Badge colorPalette={'green'} variant={'subtle'}>
                  <LuCheck /> Saved
                </Badge> : <Badge colorPalette={canEdit ? 'purple' : 'gray'} variant={'subtle'}>
                  {statusLabel}
                  {agreement.version != null ? ` · v${agreement.version}` : ''}
                </Badge>}
            </HStack>

            <Card.Root colorPalette={'purple'} size={'sm'} variant={'subtle'}>
              <Card.Body gap={2}>
                <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                  Advisory agreement
                </Text>
                <HStack align={'center'} gap={2} justify={'space-between'} wrap={'wrap'}>
                  <VStack align={'stretch'} gap={0}>
                    <Text fontWeight={'semibold'}>
                      {displayName}
                      {tierLabel !== '—' ? ` — ${tierLabel} advisor` : ''}
                    </Text>
                    <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
                      Status: {statusLabel}
                      {stageLabel !== '—' ? ` · ${stageLabel} stage` : ''}
                      {caliberLabel !== '—' ? ` · ${caliberLabel}` : ''}
                      {introLabel !== '—' && introLabel !== 'None' ? ` · ${introLabel} intros` : ''}
                    </Text>
                    {linkedSeatId ? <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                        Linked to{' '}
                        <Box as={'span'} color={'colorPalette.fg'} textDecoration={'underline'}>
                          <a href={`${paths.seats}/${linkedSeatId}`}>{linkedSeatLabel || 'board seat'}</a>
                        </Box>
                      </Text> : <Text color={'colorPalette.fg/50'} fontSize={'xs'}>
                        Not linked to a board seat
                      </Text>}
                  </VStack>
                  {canEdit ? sendBlock ? <Tooltip content={sendBlock}>
                        <Box as={'span'} display={'inline-block'}>
                          <Button disabled colorPalette={'teal'} size={'sm'} variant={'subtle'}>
                            <LuSend /> {sendLabel}
                          </Button>
                        </Box>
                      </Tooltip> : <PaywalledAdvisorAction intent={'fast-send'} upgradeCtaButtonProps={{
                  size: 'sm',
                  width: 'auto'
                }} upgradeCtaLabel={'Upgrade to send'}>
                        <Button colorPalette={'teal'} disabled={actionBusy} loading={sendMutation.loading} size={'sm'} variant={'cta'} onClick={() => void handleSend()}>
                          <LuSend /> {sendLabel}
                        </Button>
                      </PaywalledAdvisorAction> : awaitingMarkSigned ? <HStack gap={2}>
                      <Button asChild colorPalette={'teal'} size={'sm'} variant={'cta'}>
                        <a href={signHref}>Sign</a>
                      </Button>
                      {agreement.documensoDocumentId ? <Button loading={syncMutation.loading} size={'sm'} variant={'outline'} onClick={() => void handleCheckStatus()}>
                          <LuRefreshCw /> Check status
                        </Button> : null}
                    </HStack> : null}
                </HStack>
              </Card.Body>
            </Card.Root>

            <Accordion.Root bgColor={'transparent'} value={[String(activeIndex)]} onValueChange={({
            value: value_0
          }) => {
            const next_0 = value_0?.[0];
            if (next_0 != null) {
              setActiveIndex(parseInt(next_0, 10));
            }
          }}>
              <StepBlock isActive={activeIndex === 0} isComplete={recipientSectionComplete(form)} n={1} title={'Parties'} value={'0'} onContinue={() => setActiveIndex(1)} onContinueText={'Next: Engagement →'}>
                <VStack align={'stretch'} gap={5}>
                  <DocChoiceGroup disabled={!canEdit} items={INITIATING_PARTY_OPTIONS} label={'Who is sending this FAST?'} value={form.initiatingPartyKey} onChange={value_1 => {
                  setForm(prev_2 => withInitiatingParty(prev_2, normalizeInitiatingPartyKey(value_1), selfParty));
                  setSavedFlash(false);
                }} />
                  <VStack align={'stretch'} gap={4}>
                    <Text fontWeight={'semibold'}>Founder</Text>
                    <DocField label={PARTY_FIELD_COPY.founderNameLabel} placeholder={PARTY_FIELD_COPY.founderNamePlaceholder} readOnly={!canEdit} value={form.founderName} onChange={setField('founderName')} />
                    <DocField helper={FAST_FIELD_HELPERS.founderEmail} label={PARTY_FIELD_COPY.founderEmailLabel} placeholder={PARTY_FIELD_COPY.founderEmailPlaceholder} readOnly={!canEdit} type={'email'} value={form.founderEmail} onChange={setField('founderEmail')} />
                    <DocField helper={agreement?.company?.name ? `Matched to ${agreement.company.name}. Type a different name to override it.` : FAST_FIELD_HELPERS.founderCompany} label={PARTY_FIELD_COPY.founderCompanyLabel} placeholder={PARTY_FIELD_COPY.founderCompanyPlaceholder} readOnly={!canEdit} value={form.founderCompanyName} onChange={setField('founderCompanyName')} />
                  </VStack>
                  <VStack align={'stretch'} gap={4}>
                    <Text fontWeight={'semibold'}>Advisor</Text>
                    <DocField helper={FAST_FIELD_HELPERS.advisorName} label={PARTY_FIELD_COPY.advisorNameLabel} placeholder={PARTY_FIELD_COPY.advisorNamePlaceholder} readOnly={!canEdit} value={form.advisorName} onChange={setField('advisorName')} />
                    <DocField helper={FAST_FIELD_HELPERS.advisorEmail} label={PARTY_FIELD_COPY.advisorEmailLabel} placeholder={PARTY_FIELD_COPY.advisorEmailPlaceholder} readOnly={!canEdit} type={'email'} value={form.advisorEmail} onChange={setField('advisorEmail')} />
                    <DocField helper={FAST_FIELD_HELPERS.advisorCompany} label={PARTY_FIELD_COPY.advisorCompanyLabel} placeholder={PARTY_FIELD_COPY.advisorCompanyPlaceholder} readOnly={!canEdit} value={form.advisorEntityName} onChange={setField('advisorEntityName')} />
                  </VStack>
                </VStack>
              </StepBlock>

              <StepBlock isActive={activeIndex === 1} isComplete={engagementSectionComplete(form)} n={2} title={'Engagement'} value={'1'} onContinue={() => setActiveIndex(2)} onContinueText={'Next: Company & security →'}>
                <VStack align={'stretch'} gap={5}>
                  {canEdit ? <Box bg={'colorPalette.solid/8'} borderColor={'colorPalette.border'} borderRadius={'md'} borderWidth={'1px'} padding={4}>
                      <VStack align={'flex-start'} gap={3} justify={'space-between'} wrap={'wrap'}>
                        <VStack align={'stretch'} flex={'1'} gap={1} minW={'12rem'}>
                          <HStack gap={2}>
                            <Icon boxSize={4} color={'colorPalette.solid'}>
                              <LuZap />
                            </Icon>
                            <Text fontSize={'sm'} fontWeight={'semibold'}>
                              Fast track — FAST v{FAST_VERSION} standard terms
                            </Text>
                          </HStack>
                          <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                            Fills recommended equity for the selected stage and service level (Pre-seed ×
                            Strategic if unset), 5 hours per month, 24-month vest with a 3-month cliff,
                            24-month expected duration, Delaware governing law, and an effective date of the
                            1st of next month. You can still edit later steps.
                          </Text>
                        </VStack>
                        <Button flexShrink={0} size={'sm'} variant={'cta'} onClick={handleApplyStandardTerms}>
                          Apply standard terms
                        </Button>
                      </VStack>
                    </Box> : null}
                  <DocChoiceGroup disabled={!canEdit} items={areaOfAdviceChoiceItems(form.advisorTitle)} label={'Area of advice'} value={form.advisorTitle} onChange={value_2 => setFormValue('advisorTitle', value_2)} />
                  <DocChoiceGroup disabled={!canEdit} items={serviceLevelChoiceItems()} label={'Service level'} value={form.contributionTierKey} onChange={value_3 => setFormValue('contributionTierKey', value_3)} />
                </VStack>
              </StepBlock>

              <StepBlock isActive={activeIndex === 2} isComplete={companySecuritySectionComplete(form)} n={3} title={'Company & security'} value={'2'} onContinue={() => setActiveIndex(3)} onContinueText={'Next: Timing & law →'}>
                <VStack align={'stretch'} gap={5}>
                  <DocChoiceGroup disabled={!canEdit} helper={FAST_FIELD_HELPERS.companyStage} items={companyStageChoiceItems()} label={'Company stage'} value={form.companyStageKey} onChange={value_4 => setFormValue('companyStageKey', value_4)} />
                  <DocChoiceGroup disabled={!canEdit} helper={FAST_FIELD_HELPERS.securityType} items={securityTypeChoiceItems()} label={'Type of security'} value={form.securityTypeKey} onChange={value_5 => setFormValue('securityTypeKey', value_5)} />
                </VStack>
              </StepBlock>

              <StepBlock isActive={activeIndex === 3} isComplete={timingSectionComplete(form)} n={4} title={'Timing & law'} value={'3'} onContinue={() => setActiveIndex(4)} onContinueText={'Next: Equity & vesting →'}>
                <VStack align={'stretch'} gap={5}>
                  <DocDateField disabled={!canEdit} helper={FAST_FIELD_HELPERS.effectiveDate} label={'Effective date'} placeholder={'Pick a date'} value={form.startDate} onChange={value_6 => setFormValue('startDate', value_6)} />
                  <DocChoiceGroup disabled={!canEdit} helper={FAST_FIELD_HELPERS.hoursPerMonth} items={hoursPerMonthChoiceItems(form.hoursPerMonth)} label={'Expected hours / month'} value={form.hoursPerMonth} onChange={value_7 => setFormValue('hoursPerMonth', value_7)} />
                  <DocChoiceGroup disabled={!canEdit} helper={FAST_FIELD_HELPERS.termMonths} items={termMonthsChoiceItems(form.termMonths)} label={'Expected duration'} value={form.termMonths} onChange={value_8 => setFormValue('termMonths', value_8)} />
                  <DocCombobox disabled={!canEdit} items={governingLawSelectItems(form.governingLaw)} label={'Governing law'} placeholder={'Search jurisdictions…'} value={form.governingLaw} onChange={value_9 => setFormValue('governingLaw', value_9)} />
                </VStack>
              </StepBlock>

              <StepBlock isActive={activeIndex === 4} isComplete={equitySectionComplete(form)} n={5} title={'Equity & vesting'} value={'4'} onContinue={() => setActiveIndex(5)} onContinueText={'Next: Review →'}>
                <VStack align={'stretch'} gap={5}>
                  <DocField helper={FAST_FIELD_HELPERS.equityPercent} label={'Equity grant (% fully diluted)'} readOnly={!canEdit} type={'number'} value={form.equityPercent} onChange={setField('equityPercent')} />
                  <DocChoiceGroup disabled={!canEdit} helper={FAST_FIELD_HELPERS.vestingMonths} items={vestingMonthsChoiceItems(form.vestingMonths)} label={'Vesting period'} value={form.vestingMonths} onChange={value_10 => setFormValue('vestingMonths', value_10)} />
                  <DocChoiceGroup disabled={!canEdit} items={cliffMonthsChoiceItems(form.cliffMonths)} label={'Cliff'} value={form.cliffMonths} onChange={value_11 => setFormValue('cliffMonths', value_11)} />
                </VStack>
              </StepBlock>

              <StepBlock isActive={activeIndex === 5} n={6} title={'Review'} value={'5'} isComplete={recipientSectionComplete(form) && termsSectionComplete(form) && equitySectionComplete(form)}>
                <VStack align={'stretch'} gap={4} separator={<Separator />}>
                  <RecapRow k={'Parties'} v={`${form.founderName.trim() || 'Founder'} · ${previewFields.advisorName || 'Advisor'}${tierLabel !== '—' ? ` · ${tierLabel}` : ''}`} />
                  <RecapRow k={'Equity'} v={`${form.equityPercent.trim() ? `${Number(form.equityPercent).toFixed(2)}%` : formatEquityPercent(agreement.equityPercent)} ${fastSecurityTypeLabel(form.securityTypeKey)} · ${form.vestingMonths || '—'}mo vest / ${form.cliffMonths || '—'}mo cliff · accelerates on sale`} />
                  <RecapRow k={'Services'} v={`${form.advisorTitle.trim() ? `${form.advisorTitle.trim()} · ` : ''}${form.hoursPerMonth || FAST_HOURS_PER_MONTH_DEFAULT} hrs/mo expected · until ended on 5 days' notice`} />
                  <RecapRow k={'Legal'} v={FAST_LEGAL_RECAP} />
                </VStack>
                <DocTextarea disabled={!canEdit} helper={FAST_FIELD_HELPERS.additionalTerms} label={'Additional terms'} maxLength={FAST_ADDITIONAL_TERMS_MAX_LENGTH} value={form.additionalTerms} placeholder={'Optional. Anything the parties want on the paper beyond the FAST standard terms.'} onChange={setField('additionalTerms')} />
                <Separator marginY={1} />
                {saveError || sendError ? <Text color={'red.fg'} fontSize={'sm'}>
                    {saveError || sendError}
                  </Text> : sentFlash ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                    Sent to {displayName}. They can review it under FAST Agreements. Next: both of you sign.
                  </Text> : statusNote ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                    {statusNote}
                  </Text> : awaitingMarkSigned ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                    Pending Signature
                    {agreement.sentAt ? ` · sent ${formatAgreementDate(agreement.sentAt)}` : ''}.{' '}
                    {formatAgreementSigners({
                  founderName: agreement.founderName || agreement.founderUser?.fullName || agreement.founderUser?.displayName || 'Founder',
                  advisorName: advisorDisplayName(agreement),
                  founderHasSigned: agreement.founderHasSigned,
                  advisorHasSigned: agreement.advisorHasSigned
                })}
                    . Check status after you sign.
                  </Text> : canEdit && (sendBlock === SEND_PARTY_REQUIRED || sendBlock === SEND_FOUNDER_REQUIRED) ? <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                    {sendBlock}
                  </Text> : null}
                <HStack gap={2} wrap={'wrap'}>
                  {canEdit ? <Button disabled={actionBusy} loading={upsertMutation.loading && !sendMutation.loading} variant={'outline'} onClick={() => void handleSave()}>
                      Save draft
                    </Button> : null}
                  {canEdit ? sendBlock ? <Tooltip content={sendBlock}>
                        <Box as={'span'} display={'inline-block'}>
                          <Button disabled variant={'cta'}>
                            <LuSend /> {sendLabel}
                          </Button>
                        </Box>
                      </Tooltip> : <PaywalledAdvisorAction intent={'fast-send'} upgradeCtaButtonProps={{
                  width: 'auto'
                }} upgradeCtaLabel={'Upgrade to send'}>
                        <Button disabled={actionBusy} loading={sendMutation.loading} variant={'cta'} onClick={() => void handleSend()}>
                          <LuSend /> {sendLabel}
                        </Button>
                      </PaywalledAdvisorAction> : awaitingMarkSigned ? <>
                      <Button asChild variant={'cta'}>
                        <a href={signHref}>Sign</a>
                      </Button>
                      {agreement.documensoDocumentId ? <Button loading={syncMutation.loading} variant={'outline'} onClick={() => void handleCheckStatus()}>
                          <LuRefreshCw /> Check status
                        </Button> : null}
                    </> : null}
                  <Button asChild size={'sm'} variant={'ghost'}>
                    <a href={paths.agreements}>FAST Agreements</a>
                  </Button>
                  {canDelete ? <Button colorPalette={'red'} loading={deleteMutation.loading} size={'sm'} variant={'ghost'} onClick={() => void handleDelete()}>
                      <LuTrash2 /> Delete
                    </Button> : null}
                </HStack>
              </StepBlock>
            </Accordion.Root>
          </VStack>
        </Box>

        {/* live document */}
        <Box bgColor={'colorPalette.panel'} borderColor={'colorPalette.border/30'} borderLeftWidth={{
        lg: '1px'
      }} height={{
        lg: 'full'
      }} minHeight={{
        lg: 0
      }} overflowY={{
        base: 'visible',
        lg: 'auto'
      }} padding={{
        base: 5,
        md: 8
      }}>
          <HStack align={'center'} gap={2} justify={'space-between'} marginBottom={4} marginX={'auto'} maxWidth={'2xl'} wrap={'wrap'}>
            <Heading size={'md'}>Agreement preview</Heading>
            <HStack gap={2}>
              <Button loading={pdf.busyId === agreement.advisorAgreementId} size={'sm'} variant={'outline'} onClick={() => void pdf.downloadAgreementPdf(agreement.advisorAgreementId as string, 'generated')}>
                <LuDownload /> PDF
              </Button>
              <Button asChild size={'sm'} variant={'outline'}>
                <a href={calculatorHref}>
                  <LuCalculator /> Compensation Calculator
                </a>
              </Button>
            </HStack>
          </HStack>
          <Box marginX={'auto'} maxWidth={'2xl'}>
            <LegalDocument fields={previewFields} variant={'builder'} />
            {pdf.error ? <Text color={'red.fg'} fontSize={'sm'} marginTop={3}>
                {pdf.error}
              </Text> : null}
          </Box>
        </Box>
      </SimpleGrid>
    </Box>;
};